import React from 'react';
import Link from 'next/link';
import { Hit, SearchResponse } from '@algolia/client-search';
import { validateString } from 'avilatek-utils';
import { DotIcon, Table } from '@spa-cars/ui';
import { useLazyQuery } from '@apollo/client';
import { Location, PaginationInfo } from '@spa-cars/models';
import { useAlgolia, useUser } from '../../hooks';
import { GET_LOCATIONS_LIST } from '../../graphql/queries';

const buildTableData = (locations: Array<Location | Hit<Location>> = []) =>
  locations.map((location) => ({
    _id: location._id ?? (location as any).id,
    name: location?.name ?? 'N/A',
    address1: location?.address?.address1 ?? 'N/A',
    status: (
      <p className="flex gap-[10px] items-center">
        <DotIcon
          className={` ${
            location?.active ? 'text-primary-300' : ' text-danger-200'
          } w-[15px] h-[15px]`}
        />
        {location?.active ? 'Activo' : 'Almacén'}
      </p>
    ),
  }));

interface PaginationDataProps {
  items: Array<Location>;
  count: number;
  pageInfo: PaginationInfo;
}

export default function LocationTable() {
  const searchLocation = useAlgolia();
  const [user] = useUser();
  const searchIndex = searchLocation.initIndex('locations');
  const [searchResult, setSearchResult] = React.useState<Hit<Location>[]>(null);
  const [paginationData, setPaginationData] = React.useState<
    PaginationDataProps | SearchResponse<Location>
  >(null);
  const [perPage, setPerPage] = React.useState(10);
  const [searchInput, setSearchInput] = React.useState(''); // guarda lo que el usuario ingresa en el input de busqueda
  const [loadingSearch, setLoadingSearch] = React.useState(false);
  const [loadLocations, { data, loading }] = useLazyQuery<{
    locationPagination: {
      items: Array<Location>;
      count: number;
      pageInfo: PaginationInfo;
    };
  }>(GET_LOCATIONS_LIST);

  const queryVariables = {
    perPage: perPage ?? 10,
    sort: 'CREATEDAT_DESC',
    filter: {
      _id:
        user?.userType === 'admin'
          ? (user?.location as Location)?._id
          : undefined,
    },
  };

  const handleGoToPage = (value) => {
    if (validateString(searchInput)) {
      searchLocations(searchInput, value - 1); // ir a la siguiente pagina de la busqueda de algolia
    } else {
      loadLocations({
        variables: {
          page: value ?? 1,
          ...queryVariables,
        },
        fetchPolicy: 'cache-and-network',
      }); // ir a la siguiente pagina del query
    }
  };

  const columns = React.useMemo(
    () => [
      {
        Header: (
          <p className="flex justify-between items-center">
            NOMBRE
            {/* <MenuIcon className="w-5 h-5 text-neutral-100" /> */}
          </p>
        ),

        accessor: 'name',
      },
      {
        Header: (
          <p className="flex justify-between items-center">
            UBICACIÓN
            {/* <MenuIcon className="w-5 h-5 text-neutral-100" /> */}
          </p>
        ),

        accessor: 'address1',
      },
      {
        Header: (
          <p className="flex justify-between items-center">
            ESTATUS
            {/* <MenuIcon className="w-5 h-5 text-neutral-100" /> */}
          </p>
        ),
        accessor: 'status',
      },
    ],
    []
  );

  const filterResults = React.useCallback(
    (results: SearchResponse<Location>) => results?.hits ?? [],
    []
  );

  const searchLocations = async (value: string, page_: number) => {
    try {
      if (!validateString(value) || value === '') {
        loadLocations({
          variables: {
            page: 1,
            ...queryVariables,
          },
          fetchPolicy: 'cache-and-network',
        });
        setPaginationData(data?.locationPagination);
        setSearchResult([]);
        return;
      }
      setLoadingSearch(true);
      const result = await searchIndex.search<Location>(value, {
        filters:
          user?.userType === 'admin'
            ? `id:${(user?.location as Location)?._id}`
            : undefined,
        page: page_,
        hitsPerPage: perPage ?? 10,
      });
      setLoadingSearch(false);
      setPaginationData(result);
      const resultsFiltered = filterResults(result);
      setSearchResult(resultsFiltered);
    } catch (err) {
      console.log(err);
    }
  };

  const onChangeSearch = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault();
      setSearchInput(e.target.value);
    },
    []
  );

  const dataMemo = React.useMemo(
    () =>
      searchResult?.length > 0
        ? buildTableData(searchResult)
        : buildTableData((paginationData as PaginationDataProps)?.items) ?? [],
    [paginationData, searchResult]
  );

  React.useEffect(() => {
    if (data) {
      setPaginationData(data?.locationPagination);
    }
  }, [data]);

  React.useEffect(() => {
    searchLocations(searchInput, 0); // para algolia, la paginación empieza en 0
  }, [searchInput, perPage]);

  return (
    <Table
      columns={columns}
      perPage={perPage}
      setPerPage={setPerPage}
      data={dataMemo}
      error={null}
      loading={loading || loadingSearch}
      pageCount={0}
      header="Puntos de servicio"
      pageNumber={
        ((paginationData as SearchResponse<Location>)?.page ??
          ((paginationData as PaginationDataProps)?.pageInfo?.currentPage ??
            1) - 1) + 1
      }
      addButtonText="Punto de servicio"
      handleGoToPage={handleGoToPage}
      results={
        (paginationData as SearchResponse<Location>)?.nbHits ??
        (paginationData as PaginationDataProps)?.count
      }
      path="app/locations"
      link={Link}
      onChangeSearch={onChangeSearch}
    />
  );
}
